/**
 * useConversationSessions — manages one WebSocket per conversation.
 *
 * Encapsulates multi-connection lifecycle, frame handling, per-session state
 * buffering, and idle connection cleanup. Exposes a minimal API for App.tsx:
 *   - openConnection / switchToConversation / sendMessage
 *   - handleNewConversation / handleDeleteConversation
 *   - fetchConversations (REST)
 *   - Active session state (messages, agentState, status, connectionStatus)
 */
import { useRef, useState, useCallback, useEffect } from 'react';
import { authService } from './auth';
import { WS_URL, API_URL } from './common/constants';
import {
  getConversationName,
  setConversationName,
  getHiddenTurns,
  getThoughtProcess,
  setThoughtProcess,
  generateConversationName,
  loadMetadataFromApi,
  removeConversationName,
  removeHiddenTurns,
  removeThoughtProcesses,
  getTurnRating,
  setTurnRating,
  removeTurnRating,
  removeTurnRatings,
} from './local-state';
import { describeToolCall } from './describe-tool-call';
import { fetchJson } from './fetchJson';
import type {
  ClientFrame,
  ServerFrame,
  ConversationEntry,
  TurnArtifact,
  Artifact,
} from '@amzn/soda-brain-protocol';

// ─── Types ───

/**
 * Discriminated union for a turn's feedback state (decision 12 of the
 * turn-feedback spec). Every `pending` variant carries exactly what its own
 * resolution needs: `'rate'` remembers only the `target` it's waiting to
 * be acked (a fresh/plain rating attempt has nothing to revert to on
 * failure — it just goes back to unset); `'deselect'` remembers `prior`
 * (the revert target if the delete's ack comes back `'failed'`);
 * `'switch'` remembers both `prior` (revert target if the deselect half
 * fails) and `target` (what to send once that half acks `'removed'`), plus
 * an optional `comment` for a down-switch — collected by TurnFeedback.tsx
 * BEFORE this state is even entered (decision 14), so `handleFrame` never
 * needs to pause mid-transition to wait on user input.
 */
export type MessageFeedback =
  | { rating: 'up' | 'down' }
  | { pending: 'rate'; target: 'up' | 'down' }
  | { pending: 'deselect'; prior: 'up' | 'down' }
  | { pending: 'switch'; prior: 'up' | 'down'; target: 'up' | 'down'; comment?: string };

export interface Message {
  text: string;
  isUser: boolean;
  timestamp: Date;
  thoughtProcess?: string[];
  turnNumber?: number;
  /**
   * Stable turn identity, present only for a successfully-persisted
   * completed agent turn (mirrors ResponseFrame/TurnArtifact's optional
   * `turnId` — see @amzn/soda-brain-protocol). Absent for user messages,
   * action-card responses, clarification questions, and any turn predating
   * this field. `TurnFeedback.tsx` uses absence as its mount guard — no
   * `turnId` means this message is never eligible for a `feedback` frame.
   */
  turnId?: string;
  /**
   * Feedback state for this turn, as a discriminated union (decision 12 of
   * the turn-feedback spec — supersedes an earlier three-state
   * `{ rating } | { pending: true }` design that could not represent a
   * switch's intermediate state). `undefined` means unset (never rated, or
   * deselected). Every `pending` variant means exactly one thing: a frame
   * is already in flight, awaiting its `feedbackAck` — never "waiting on
   * user input" (decision 14; see TurnFeedback.tsx's down-switch handling,
   * which collects its comment BEFORE entering any pending state for
   * exactly this reason). Lives on `Message`/session state rather than
   * `TurnFeedback` component-local state specifically so a conversation
   * switch that unmounts the component doesn't orphan an in-flight
   * operation — `handleFrame`'s `feedbackAck` case (below) owns resolving
   * every variant, including sending a switch's second frame, so nothing
   * component-scoped needs to survive the unmount. See SodaBrainProtocol's
   * turn-feedback spec §7 for the full design.
   */
  feedback?: MessageFeedback;
  /** Machine-produced turn artifacts (e.g. download links) delivered out-of-band from the answer text. */
  artifacts?: Artifact[];
  /**
   * True for a message pushed from a `clarification` frame — the agent
   * paused execution to ask this question, and the user's next reply
   * resumes rather than starts a fresh turn. Set once and never cleared,
   * so the marker stays on this message in history even after the pause
   * resolves and `agentState` moves on (see clarification-visual-affordance.md).
   */
  isClarification?: boolean;
}

export type AgentState = 'idle' | 'executing' | 'awaitingClarification';
export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected';

export interface ConversationSession {
  ws: WebSocket | null;
  connectionStatus: ConnectionStatus;
  agentState: AgentState;
  messages: Message[];
  thoughtProcess: string[];
  status: string;
  requestStartTime: number;
  lastViewedAt: number;
}

export interface UseConversationSessionsReturn {
  /** The ID of the currently-viewed conversation (null = new conversation pending) */
  activeConversationId: string | null;
  /** Conversation list for sidebar (fetched via REST) */
  conversations: ConversationEntry[];
  /** Active session state — re-renders when active session changes */
  messages: Message[];
  agentState: AgentState;
  status: string;
  connectionStatus: ConnectionStatus;
  /** Actions */
  sendMessage: (text: string, presetName?: string) => void;
  sendFrame: (frame: ClientFrame) => void;
  cancelExecution: () => void;
  handleNewConversation: () => void;
  handleDeleteConversation: (id: string) => Promise<boolean>;
  switchToConversation: (id: string) => void;
  /** Returns the freshly-fetched list directly (in addition to updating `conversations` state), so callers that need it immediately — e.g. the boot sequence deciding whether to auto-open the most recent conversation — don't have to read possibly-stale state from the same render. */
  fetchConversations: () => Promise<ConversationEntry[]>;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  setConversations: React.Dispatch<React.SetStateAction<ConversationEntry[]>>;
}

// ─── Constants ───

const PING_INTERVAL_MS = 30_000;
const IDLE_CLEANUP_MS = 5 * 60 * 1000;
const CLEANUP_CHECK_MS = 60_000;
/**
 * Bounded wait for a switch's second `feedbackAck` before reverting a
 * stuck `{ pending: 'rate' }` back to unset (decision 12's continuation is
 * hook-owned specifically so a component unmount can't orphan it — but
 * that same hook ownership means `TurnFeedback.tsx`'s own disconnect-race
 * watchdog, `ACK_TIMEOUT_MS`, never covers this particular frame, since it
 * was never sent from that component. This is the hook-level equivalent,
 * armed at the same send site, for the one frame the component doesn't
 * send itself). Matches `TurnFeedback.tsx`'s `ACK_TIMEOUT_MS` value so the
 * two timeouts read as one consistent product behavior, not two different
 * numbers a reader has to reconcile.
 */
const SWITCH_SECOND_FRAME_ACK_TIMEOUT_MS = 8_000;

/**
 * Maps the current agent state to the ClientFrame sendMessage should send.
 * A single switch on AgentState so the mapping is decided in exactly one
 * place — TypeScript flags a missing case here if AgentState grows a fourth
 * value, which a two-step (action-string then re-derive) version would not.
 */
function frameFor(state: AgentState, text: string): ClientFrame {
  switch (state) {
    case 'executing':
      return { action: 'steer', text };
    case 'awaitingClarification':
      return { action: 'clarificationResponse', text };
    case 'idle':
      return { action: 'message', text };
  }
}

// ─── Hook ───

export function useConversationSessions(
  onConversationLoaded?: (conversationId: string) => void,
): UseConversationSessionsReturn {
  // Active conversation — null means "new conversation pending" (send button disabled)
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const activeConversationIdRef = useRef<string | null>(null);

  // Conversation list (populated via REST)
  const [conversations, setConversations] = useState<ConversationEntry[]>([]);

  // Render trigger — increment to force re-render when active session state changes
  const [, setRenderTick] = useState(0);
  const triggerRender = useCallback(() => setRenderTick(t => t + 1), []);

  // Ref so handleFrame (a plain function, not a hook) always sees the latest
  // callback without needing to be re-created on every render.
  const onConversationLoadedRef = useRef(onConversationLoaded);
  onConversationLoadedRef.current = onConversationLoaded;

  // Session map (mutable ref — no re-render on background session updates)
  const sessions = useRef<Map<string, ConversationSession>>(new Map());

  // Ping intervals per connection
  const pingIntervals = useRef<Map<WebSocket, ReturnType<typeof setInterval>>>(new Map());

  // Disconnect-race watchdogs for a switch's second feedback frame, keyed
  // by turnId — see SWITCH_SECOND_FRAME_ACK_TIMEOUT_MS's doc comment for
  // why this exists at the hook level rather than in TurnFeedback.tsx.
  const switchAckTimeouts = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Track the pending "new conversation" session before it has an ID
  const pendingNewSession = useRef<ConversationSession | null>(null);

  // Preset title to apply to the next new conversation (e.g. the roadmap
  // button labels its conversation to match the button text). Consumed once
  // in the conversationCreated handler, then cleared.
  const pendingNameForNewSession = useRef<string | null>(null);

  // ─── Helpers ───

  /** Get the active session (or null) */
  function getActiveSession(): ConversationSession | null {
    if (!activeConversationIdRef.current) return pendingNewSession.current;
    return sessions.current.get(activeConversationIdRef.current) ?? null;
  }

  /** Get a fresh auth token, refreshing if needed. Returns null if refresh fails. */
  async function getValidToken(): Promise<string | null> {
    if (authService.isTokenExpired()) {
      const refreshed = await authService.refreshTokens();
      if (!refreshed) {
        authService.login();
        return null;
      }
    }
    return authService.getIdToken();
  }

  function startPing(ws: WebSocket): void {
    const interval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ action: 'ping' }));
      }
    }, PING_INTERVAL_MS);
    pingIntervals.current.set(ws, interval);
  }

  function stopPing(ws: WebSocket): void {
    const interval = pingIntervals.current.get(ws);
    if (interval) {
      clearInterval(interval);
      pingIntervals.current.delete(ws);
    }
  }

  /** Convert TurnArtifact[] to Message[], respecting hidden turns */
  function turnsToMessages(conversationId: string, turns: TurnArtifact[]): Message[] {
    const hidden = getHiddenTurns(conversationId);
    return turns
      .filter(t => !hidden.includes(t.turnNumber))
      .flatMap((turn) => {
        // Response frames use 0-indexed turnNumber, S3 artifacts use 1-indexed.
        // Check both to handle the mismatch.
        const storedThoughts = getThoughtProcess(conversationId, turn.turnNumber)
          ?? getThoughtProcess(conversationId, turn.turnNumber - 1);
        // Fallback for devices/browsers with no localStorage record (new device,
        // cleared cache, shared link): reconstruct a smaller trace from the
        // persisted toolCalls rather than showing nothing. This won't include
        // tool_result/thinking steps or the elapsed-time footer that a live
        // localStorage-backed trace has — those aren't in TurnArtifact.
        //
        // turn.toolCalls may be absent on turns persisted before this field
        // existed — loadConversation deserializes S3 JSON via an unchecked
        // `as TurnArtifact` cast (conversation-store.ts), so old records don't
        // actually satisfy today's required-field contract at runtime.
        const toolCalls = turn.toolCalls ?? [];
        const thoughtProcess = storedThoughts ?? toolCalls.map(describeToolCall);
        // Restores a same-browser rated state on reload — see local-state.ts's
        // setTurnRating/getTurnRating doc comment for what this does and does
        // NOT achieve (no cross-device sync). Only meaningful when turn.turnId
        // is present — a turn with no turnId (predates this feature, or was
        // failed/cancelled) has nothing to look up and TurnFeedback.tsx's own
        // mount guard on an absent turnId means this would be unused anyway.
        const storedRating = turn.turnId ? getTurnRating(conversationId, turn.turnId) : undefined;
        return [
          { text: turn.userMessage, isUser: true, timestamp: new Date(turn.timestamp), turnNumber: turn.turnNumber },
          {
            text: turn.answer,
            isUser: false,
            timestamp: new Date(turn.timestamp),
            turnNumber: turn.turnNumber,
            turnId: turn.turnId,
            feedback: storedRating ? { rating: storedRating } : undefined,
            artifacts: turn.artifacts,
            thoughtProcess,
          },
        ];
      });
  }

  // ─── Frame Handler ───

  function handleFrame(session: ConversationSession, conversationId: string, frame: ServerFrame): void {
    switch (frame.type) {
      case 'conversationCreated': {
        // Bind this session to its new ID
        sessions.current.set(frame.conversationId, session);
        pendingNewSession.current = null;
        setActiveConversationId(frame.conversationId);
        activeConversationIdRef.current = frame.conversationId;
        // Apply a preset title (e.g. from the roadmap button) now, before the
        // first answer triggers the auto-namer — the auto-namer only names a
        // conversation that has no name yet, so setting it here wins.
        if (pendingNameForNewSession.current) {
          setConversationName(frame.conversationId, pendingNameForNewSession.current);
          pendingNameForNewSession.current = null;
        }
        // Add to sidebar
        setConversations(prev => [{
          conversationId: frame.conversationId,
          createdAtUtc: new Date().toISOString(),
          lastActiveAtUtc: new Date().toISOString(),
        }, ...prev]);
        triggerRender();
        break;
      }

      case 'conversationLoaded': {
        const loadedMessages = turnsToMessages(frame.conversationId, frame.turns);
        // eslint-disable-next-line no-console
        console.debug(`[convo] conversationLoaded(${frame.conversationId}): server sent ${frame.turns.length} turn(s), ${loadedMessages.length} rendered after hidden-turn filtering`);
        if (loadedMessages.length > 0) {
          // Server has history — use it (ground truth, may include turns completed while disconnected)
          session.messages = loadedMessages;
        } else if (frame.turns.length > 0) {
          // eslint-disable-next-line no-console
          console.warn(`[convo] conversationLoaded(${frame.conversationId}): server sent ${frame.turns.length} turn(s) but ALL were filtered out as hidden — this is the "empty chat" symptom if this conversation isn't actually fully hidden`);
        }
        // If server returns empty turns but session already has messages (e.g., user's
        // first message on a brand-new conversation), preserve the existing messages
        if (frame.isExecuting) {
          session.agentState = 'executing';
          session.status = 'Resuming — waiting for next update...';
        }
        // Unblock the boot sequence / render immediately on the correctness-critical
        // data (turns). Metadata (display names, hidden turns, cached thought-process
        // text) is cosmetic enrichment — it hydrates in the background below and must
        // not gate readiness (see startup-sequencing spec §2, §6).
        onConversationLoadedRef.current?.(frame.conversationId);
        if (frame.conversationId === activeConversationIdRef.current) {
          triggerRender();
        }

        loadMetadataFromApi(frame.conversationId).then(() => {
          if (frame.conversationId === activeConversationIdRef.current) {
            triggerRender();
          }
        });
        break;
      }

      case 'progress': {
        session.agentState = 'executing';
        if (frame.step === 'tool_call') {
          const description = describeToolCall(frame.tool);
          session.status = description;
          session.thoughtProcess = [...session.thoughtProcess, description];
        } else {
          session.status = frame.message;
          const formatted = frame.message.replace(/```(\w*)\n/g, '\n```$1\n');
          session.thoughtProcess = [...session.thoughtProcess, formatted];
        }
        if (conversationId === activeConversationIdRef.current) {
          triggerRender();
        }
        break;
      }

      case 'steerAck': {
        session.thoughtProcess = [...session.thoughtProcess, '🔀 Steering acknowledged'];
        if (conversationId === activeConversationIdRef.current) {
          triggerRender();
        }
        break;
      }

      case 'cancelAck': {
        session.agentState = 'idle';
        session.status = '';
        session.thoughtProcess = [];
        if (conversationId === activeConversationIdRef.current) {
          triggerRender();
        }
        break;
      }

      case 'response': {
        session.agentState = 'idle';
        session.status = '';
        const { data } = frame;
        const thoughts = session.thoughtProcess;
        const elapsedMs = Date.now() - session.requestStartTime;
        const mins = Math.floor(elapsedMs / 60000);
        const secs = Math.floor((elapsedMs % 60000) / 1000);
        const elapsed = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
        if (thoughts.length > 0) thoughts.push(`**⏱️ Response time: ${elapsed}**`);

        session.messages = [
          ...session.messages,
          {
            text: data.answer,
            isUser: false,
            timestamp: new Date(),
            thoughtProcess: thoughts.length > 0 ? [...thoughts] : undefined,
            turnNumber: frame.turnNumber,
            turnId: frame.turnId,
            artifacts: data.artifacts,
          },
        ];

        // Auto-name from first user message
        if (conversationId && !getConversationName(conversationId)) {
          const firstUserMsg = session.messages.find(m => m.isUser);
          if (firstUserMsg) {
            setConversationName(conversationId, generateConversationName(firstUserMsg.text));
          }
        }

        // Persist thought process to localStorage
        if (thoughts.length > 0 && conversationId && frame.turnNumber !== undefined) {
          setThoughtProcess(conversationId, frame.turnNumber, thoughts);
        }

        session.thoughtProcess = [];

        // Update sidebar preview
        setConversations(prev => prev.map(c =>
          c.conversationId === conversationId
            ? { ...c, lastAnswer: data.answer.replace(/[#*`_[\]]/g, '').slice(0, 100), lastActiveAtUtc: new Date().toISOString() }
            : c
        ));

        if (conversationId === activeConversationIdRef.current) {
          triggerRender();
        }
        break;
      }

      case 'error': {
        session.agentState = 'idle';
        session.status = '';
        session.messages = [...session.messages, {
          text: `❌ ${frame.message}`,
          isUser: false,
          timestamp: new Date(),
        }];
        session.thoughtProcess = [];
        if (conversationId === activeConversationIdRef.current) {
          triggerRender();
        }
        break;
      }

      case 'feedbackAck': {
        // Correlated by turnId, NOT routed through the shared 'error' case
        // above — this is exactly why feedbackAck exists as its own frame
        // type rather than a generic ErrorFrame on failure: this handler
        // touches only the one Message matching frame.turnId, never
        // session.agentState/session.messages wholesale, so it cannot
        // corrupt an unrelated turn's UI even if that turn is actively
        // streaming on the same connection (see SodaBrainProtocol's
        // turn-feedback spec, Decisions Log entries 1 and 3).
        const index = session.messages.findIndex(m => m.turnId === frame.turnId);
        if (index === -1) {
          // Explicit no-op, not a silent fall-through: the ack can race a
          // conversation switch, or arrive for a turn the user has since
          // hidden — in either case there is no Message to update, and
          // that's expected, not an error condition to log.
          break;
        }

        const updated = [...session.messages];
        const priorFeedback = updated[index].feedback;

        // Any terminal or intermediate ack for this turnId supersedes a
        // previously-armed switch-second-frame watchdog — whichever
        // resolution reaches here first, a stale timer from an earlier
        // send must not fire later and clobber a since-resolved state.
        const existingSwitchTimeout = switchAckTimeouts.current.get(frame.turnId);
        if (existingSwitchTimeout) {
          clearTimeout(existingSwitchTimeout);
          switchAckTimeouts.current.delete(frame.turnId);
        }

        if (frame.status === 'ok') {
          // Terminal state for both a fresh rating AND the second half of
          // a switch — no lingering distinction to preserve past this
          // point (decision 12).
          updated[index] = { ...updated[index], feedback: { rating: frame.rating } };
          setTurnRating(conversationId, frame.turnId, frame.rating);
        } else if (frame.status === 'removed') {
          // Intermediate ack for a switch (send the second frame right
          // here, from the hook — never from TurnFeedback.tsx, so a
          // conversation-switch unmount can't orphan it) vs. terminal ack
          // for a plain deselect. Both cases clear local-state's
          // same-browser cache: the server-side row is genuinely gone at
          // this exact moment either way (decision 12).
          removeTurnRating(conversationId, frame.turnId);
          if (priorFeedback && 'pending' in priorFeedback && priorFeedback.pending === 'switch') {
            const { target, comment } = priorFeedback;
            updated[index] = { ...updated[index], feedback: { pending: 'rate', target } };
            // Matches sendFrame's own readyState check (this file's other
            // send sites all guard the same way) — sending on a closing/
            // closed socket throws, and this call isn't otherwise wrapped
            // in a try/catch. If the socket isn't open, skip the send and
            // let the disconnect-race timeout below revert the stuck
            // pending state once it fires — the same recoverable-by-design
            // path TurnFeedback.tsx already relies on for every other
            // frame this feature sends.
            if (session.ws && session.ws.readyState === WebSocket.OPEN) {
              session.ws.send(JSON.stringify({ action: 'feedback', turnId: frame.turnId, rating: target, comment }));
            }
            // Disconnect-race watchdog for THIS frame specifically: this
            // send happens here, at the hook level, precisely so a
            // component unmount can't orphan it (decision 12) — but that
            // same design means TurnFeedback.tsx's own ACK_TIMEOUT_MS
            // watchdog never covers it, since it was never armed by that
            // component for this frame. Without this, a lost ack (or a
            // socket that was already closed above) would leave the turn
            // stuck at { pending: 'rate' } forever. Looks the session back
            // up by conversationId at fire time (not the closed-over
            // `session` reference) for the same reason the idle-cleanup
            // effect elsewhere in this file does: this callback can fire
            // long after this render, and the session object it captured
            // may no longer be the live one for that conversationId.
            const capturedTurnId = frame.turnId;
            const capturedConversationId = conversationId;
            const timeout = setTimeout(() => {
              switchAckTimeouts.current.delete(capturedTurnId);
              const liveSession = sessions.current.get(capturedConversationId);
              if (!liveSession) return;
              liveSession.messages = liveSession.messages.map(m =>
                m.turnId === capturedTurnId && m.feedback && 'pending' in m.feedback ? { ...m, feedback: undefined } : m,
              );
              if (capturedConversationId === activeConversationIdRef.current) {
                triggerRender();
              }
            }, SWITCH_SECOND_FRAME_ACK_TIMEOUT_MS);
            switchAckTimeouts.current.set(capturedTurnId, timeout);
          } else {
            updated[index] = { ...updated[index], feedback: undefined };
          }
        } else {
          // frame.status === 'failed' — revert using whatever the
          // pre-failure state already recorded, per its own rule
          // (decision 12): a fresh/plain rate attempt had nothing before
          // it, so it reverts to unset; a deselect or a switch's first
          // half reverts to the rating that was locked before this click;
          // a switch's second half is, by this point, already
          // { pending: 'rate', target } (set above when its first half
          // acked 'removed') and therefore falls into the same "revert to
          // unset" rule as any other 'rate' failure — the delete already
          // succeeded, so the turn is genuinely unrated server-side, and
          // reverting to `prior` here would show a rating the server no
          // longer has a row for.
          const revertTo: MessageFeedback | undefined =
            priorFeedback && 'pending' in priorFeedback && (priorFeedback.pending === 'deselect' || priorFeedback.pending === 'switch')
              ? { rating: priorFeedback.prior }
              : undefined;
          updated[index] = { ...updated[index], feedback: revertTo };
        }
        session.messages = updated;

        if (conversationId === activeConversationIdRef.current) {
          triggerRender();
        }
        break;
      }

      case 'clarification': {
        // Execution paused, not completed — no turnNumber, no sidebar preview
        // update, no persisted thoughtProcess: this isn't a saved turn.
        // isClarification marks the message itself (not just agentState) so
        // it stays visually distinct in history after the pause resolves.
        session.agentState = 'awaitingClarification';
        session.status = '';
        session.messages = [...session.messages, {
          text: frame.question,
          isUser: false,
          timestamp: new Date(),
          isClarification: true,
        }];
        session.thoughtProcess = [];
        if (conversationId === activeConversationIdRef.current) {
          triggerRender();
        }
        break;
      }

      case 'pong':
        // Keepalive response — no action needed
        break;
    }
  }

  // ─── Connection Management ───

  /** Reconnect a disconnected session — opens new WS but preserves existing messages/state */
  async function reconnectSession(conversationId: string, session: ConversationSession): Promise<void> {
    const token = await getValidToken();
    if (!token) {
      return;
    }

    const ws = new WebSocket(`${WS_URL}?token=${encodeURIComponent(token)}&conversationId=${encodeURIComponent(conversationId)}`);
    session.ws = ws;
    session.connectionStatus = 'connecting';

    ws.onopen = () => {
      session.connectionStatus = 'connected';
      startPing(ws);
      if (conversationId === activeConversationIdRef.current) {
        triggerRender();
      }
    };

    ws.onmessage = (event) => {
      const frame = JSON.parse(event.data) as ServerFrame;
      handleFrame(session, conversationId, frame);
    };

    ws.onclose = () => {
      session.connectionStatus = 'disconnected';
      session.ws = null;
      stopPing(ws);
      if (session.agentState === 'executing') {
        session.status = 'Connection lost — will resume on reconnect...';
      }
      if (conversationId === activeConversationIdRef.current) {
        triggerRender();
      }
    };

    ws.onerror = () => {
      session.connectionStatus = 'disconnected';
    };
  }

  /** Open a WebSocket for a specific conversation (or new=true) */
  async function openConnection(conversationId: string | null): Promise<ConversationSession | null> {
    const token = await getValidToken();
    if (!token) {
      return null;
    }

    const params = conversationId
      ? `conversationId=${encodeURIComponent(conversationId)}`
      : 'new=true';
    const wsUrl = `${WS_URL}?token=${encodeURIComponent(token)}&${params}`;
    // eslint-disable-next-line no-console
    console.debug(`[convo] openConnection(${conversationId}): opening WebSocket`);
    const ws = new WebSocket(wsUrl);

    // For new conversations, reuse the placeholder session (preserves queued user message)
    const session: ConversationSession = (!conversationId && pendingNewSession.current)
      ? pendingNewSession.current
      : {
        ws,
        connectionStatus: 'connecting',
        agentState: 'idle',
        messages: [],
        thoughtProcess: [],
        status: '',
        requestStartTime: 0,
        lastViewedAt: Date.now(),
      };

    // Attach the WebSocket to the session
    session.ws = ws;
    session.connectionStatus = 'connecting';

    // Track the effective conversationId — mutable so conversationCreated can update it
    let boundConversationId = conversationId;

    ws.onopen = () => {
      // eslint-disable-next-line no-console
      console.debug(`[convo] openConnection(${conversationId}): WebSocket connected`);
      session.connectionStatus = 'connected';
      startPing(ws);
      if (boundConversationId === activeConversationIdRef.current || !boundConversationId) {
        triggerRender();
      }
    };

    ws.onmessage = (event) => {
      const frame = JSON.parse(event.data) as ServerFrame;
      // Excludes 'pong' — logged every PING_INTERVAL_MS and adds no diagnostic
      // value for the conversation-loading investigation this logging supports.
      if (frame.type !== 'pong') {
        // eslint-disable-next-line no-console
        console.debug(`[convo] openConnection(${conversationId}): received frame type=${frame.type}` +
          (frame.type === 'conversationLoaded' ? ` turns=${frame.turns.length} isExecuting=${frame.isExecuting}` : ''));
      }
      // For new conversations, conversationCreated updates boundConversationId
      if (frame.type === 'conversationCreated') {
        boundConversationId = frame.conversationId;
      }
      handleFrame(session, boundConversationId ?? '', frame);
    };

    ws.onclose = () => {
      // eslint-disable-next-line no-console
      console.debug(`[convo] openConnection(${conversationId}): WebSocket closed`);
      session.connectionStatus = 'disconnected';
      session.ws = null;
      stopPing(ws);
      if (session.agentState === 'executing') {
        session.status = 'Connection lost — will resume on reconnect...';
      }
      if (boundConversationId === activeConversationIdRef.current || !boundConversationId) {
        triggerRender();
      }
    };

    ws.onerror = () => {
      // eslint-disable-next-line no-console
      console.error(`[convo] openConnection(${conversationId}): WebSocket error`);
      session.connectionStatus = 'disconnected';
    };

    // Store in the appropriate place
    if (conversationId) {
      sessions.current.set(conversationId, session);
    } else {
      pendingNewSession.current = session;
    }

    return session;
  }

  // ─── Public API ───

  /**
   * Fetch conversation list via REST.
   *
   * Throws on a failed token refresh, a non-ok response, or a network
   * error — callers (the loadingWorkspace boot phase) need to distinguish
   * "fetch failed" from "fetch succeeded with an empty list" in order to
   * surface a retry instead of silently treating a failure as "no
   * conversations yet." Returns the fetched list directly (in addition to
   * updating `conversations` state) so a caller in the same tick — e.g. the
   * boot sequence deciding whether to auto-open the most recent
   * conversation — isn't reading stale pre-fetch state.
   */
  const fetchConversations = useCallback(async (): Promise<ConversationEntry[]> => {
    const token = await getValidToken();
    const data = await fetchJson<{ conversations?: ConversationEntry[] } | ConversationEntry[]>(
      `${API_URL}/conversations`,
      token,
      'conversations',
    );
    const fetched: ConversationEntry[] = Array.isArray(data) ? data : (data.conversations ?? []);
    setConversations(fetched);
    return fetched;
  }, []);

  /** Switch the active view to a conversation (opens connection if needed) */
  const switchToConversation = useCallback((id: string) => {
    // eslint-disable-next-line no-console
    console.debug(`[convo] switchToConversation(${id}) called`);
    setActiveConversationId(id);
    activeConversationIdRef.current = id;

    const session = sessions.current.get(id);
    if (session) {
      session.lastViewedAt = Date.now();
      // Already connected — just re-render to show this session's state
      if (session.connectionStatus === 'connected' || session.connectionStatus === 'connecting') {
        // eslint-disable-next-line no-console
        console.debug(`[convo] switchToConversation(${id}): reusing ${session.connectionStatus} session, ${session.messages.length} message(s) already loaded — no new fetch will happen`);
        triggerRender();
        return;
      }
      // Disconnected but has buffered messages — re-render immediately (shows cached state),
      // then reconnect in the background to resume receiving frames
      if (session.messages.length > 0) {
        // eslint-disable-next-line no-console
        console.debug(`[convo] switchToConversation(${id}): disconnected session with ${session.messages.length} buffered message(s), reconnecting in background`);
        triggerRender();
        // Reconnect: open new WS but attach to existing session
        reconnectSession(id, session);
        return;
      }
      // eslint-disable-next-line no-console
      console.debug(`[convo] switchToConversation(${id}): existing session found but disconnected with 0 messages — falling through to openConnection`);
    } else {
      // eslint-disable-next-line no-console
      console.debug(`[convo] switchToConversation(${id}): no existing session, opening a fresh connection`);
    }

    // No session at all — open a fresh connection
    openConnection(id);
    triggerRender();
  }, [triggerRender]);

  /** Start a new conversation (disables send until conversationCreated arrives) */
  const handleNewConversation = useCallback(() => {
    setActiveConversationId(null);
    activeConversationIdRef.current = null;
    triggerRender();
  }, [triggerRender]);

  /**
   * Delete a conversation via REST + close WebSocket.
   *
   * Returns whether the delete actually succeeded. The sidebar entry is only
   * removed on a confirmed successful DELETE — previously this removed the
   * conversation from local state unconditionally, so a dead auth token
   * (getValidToken() returning null, or the DELETE request itself failing)
   * silently skipped the server-side delete while the UI still showed it as
   * gone, until the next reload re-fetched the real (undeleted) list from
   * the server. Local cleanup (name/hidden-turns/thought-process) is also
   * gated on success for the same reason: it's about this same conversation
   * having genuinely been deleted, not a client-side "hide" independent of
   * server state.
   */
  const handleDeleteConversation = useCallback(async (id: string): Promise<boolean> => {
    const session = sessions.current.get(id);

    // REST delete — must succeed before any local state (including the
    // WebSocket connection itself) is mutated. Closing the socket
    // unconditionally here, before the request even resolves, was the bug
    // AutoSDE caught on CR-297822669: a failed delete left the session
    // technically still in sessions.current (correctly, since delete()
    // failed) but with an already-closed ws — silently breaking a
    // conversation's connection as a side effect of an action that didn't
    // actually succeed.
    const token = await getValidToken();
    if (!token) {
      // eslint-disable-next-line no-console
      console.warn(`[convo] handleDeleteConversation(${id}): no valid token, delete NOT sent to server`);
      return false;
    }

    let deleteSucceeded = false;
    try {
      const response = await fetch(`${API_URL}/conversations/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      deleteSucceeded = response.ok;
      if (!response.ok) {
        // eslint-disable-next-line no-console
        console.warn(`[convo] handleDeleteConversation(${id}): server returned ${response.status}, not removing locally`);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(`[convo] handleDeleteConversation(${id}): DELETE request failed`, error);
    }

    if (!deleteSucceeded) {
      return false;
    }

    // Only close the WebSocket and drop the session once the server has
    // confirmed the delete — a failed delete must leave the connection
    // fully intact and functional.
    if (session?.ws) {
      session.ws.close();
    }
    sessions.current.delete(id);

    // Clean up local state
    removeConversationName(id);
    removeHiddenTurns(id);
    removeThoughtProcesses(id);
    removeTurnRatings(id);

    // Remove from sidebar
    setConversations(prev => prev.filter(c => c.conversationId !== id));

    // If active, switch to another
    if (activeConversationIdRef.current === id) {
      setActiveConversationId(null);
      activeConversationIdRef.current = null;
      triggerRender();
    }

    return true;
  }, [triggerRender]);

  /** Send a text message (determines frame type from agent state) */
  const sendMessage = useCallback((text: string, presetName?: string) => {
    if (!text.trim()) return;

    const session = getActiveSession();

    // If no active conversation and no pending session, create a new one
    if (!activeConversationIdRef.current && !pendingNewSession.current) {
      // Synchronously claim the slot to prevent duplicate connections on rapid double-click
      const placeholder: ConversationSession = {
        ws: null,
        connectionStatus: 'connecting',
        agentState: 'executing',
        messages: [{ text, isUser: true, timestamp: new Date() }],
        thoughtProcess: [],
        status: 'Creating conversation...',
        requestStartTime: Date.now(),
        lastViewedAt: Date.now(),
      };
      pendingNewSession.current = placeholder;
      pendingMessageForNewSession.current = text;
      // Preset title only applies to a brand-new conversation; captured here
      // and consumed in the conversationCreated handler.
      pendingNameForNewSession.current = presetName ?? null;
      triggerRender();

      openConnection(null).then((newSession) => {
        if (!newSession) {
          pendingNewSession.current = null;
          pendingMessageForNewSession.current = null;
          triggerRender();
          return;
        }
        // openConnection already set pendingNewSession to the real session
        // Messages and state are transferred via conversationCreated handler
      });
      return;
    }

    if (!session?.ws || session.ws.readyState !== WebSocket.OPEN) return;

    const currentState = session.agentState;

    // Exhaustive on AgentState so a future fourth state can't silently fall
    // through to the wrong frame — TypeScript flags a missing case in frameFor.
    const frame: ClientFrame = frameFor(currentState, text);

    session.ws.send(JSON.stringify(frame));

    // Update local state. Local bookkeeping only differs for 'message': it
    // starts a fresh execution (agentState -> executing, reset timers/status).
    // 'steer' and 'clarificationResponse' both just redirect/resume an
    // execution the backend is already tracking, so the frontend only needs
    // to display the user's text — the backend's next frame decides what
    // state comes next.
    session.messages = [...session.messages, { text, isUser: true, timestamp: new Date() }];
    if (frame.action === 'message') {
      session.agentState = 'executing';
      session.requestStartTime = Date.now();
      session.thoughtProcess = [];
      session.status = 'Processing...';
    }

    triggerRender();
  }, [triggerRender]);

  /** Send an arbitrary typed frame (for action cards) */
  const sendFrame = useCallback((frame: ClientFrame) => {
    const session = getActiveSession();
    if (!session?.ws || session.ws.readyState !== WebSocket.OPEN) return;
    session.ws.send(JSON.stringify(frame));
  }, []);

  /** Cancel the active execution */
  const cancelExecution = useCallback(() => {
    const session = getActiveSession();
    if (!session?.ws || session.ws.readyState !== WebSocket.OPEN) return;
    if (session.agentState !== 'executing') return;
    session.ws.send(JSON.stringify({ action: 'cancel' }));
  }, []);

  /** setMessages for the active session (used by hide turn and optimistic feedback state) */
  const setMessages: React.Dispatch<React.SetStateAction<Message[]>> = useCallback((action) => {
    const session = getActiveSession();
    if (!session) return;
    if (typeof action === 'function') {
      session.messages = action(session.messages);
    } else {
      session.messages = action;
    }
    triggerRender();
  }, [triggerRender]);

  // ─── Pending message for new conversations ───
  const pendingMessageForNewSession = useRef<string | null>(null);

  // Watch for conversationCreated on a new session that has a pending message.
  // When conversationCreated fires, activeConversationId is set, triggering this effect.
  useEffect(() => {
    if (!activeConversationIdRef.current || !pendingMessageForNewSession.current) return;
    const session = sessions.current.get(activeConversationIdRef.current);
    if (!session?.ws || session.ws.readyState !== WebSocket.OPEN) return;

    const pending = pendingMessageForNewSession.current;
    pendingMessageForNewSession.current = null;

    // Session already has the user message in messages[] (added in sendMessage)
    session.ws.send(JSON.stringify({ action: 'message', text: pending }));
    session.agentState = 'executing';
    session.requestStartTime = Date.now();
    session.status = 'Processing...';
    triggerRender();
  }, [activeConversationId]);

  // ─── Idle Connection Cleanup ───

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      for (const [id, session] of sessions.current) {
        if (id === activeConversationIdRef.current) continue;
        if (session.agentState !== 'idle') continue;
        if (now - session.lastViewedAt > IDLE_CLEANUP_MS) {
          session.ws?.close();
          session.ws = null;
          session.connectionStatus = 'disconnected';
        }
      }
    }, CLEANUP_CHECK_MS);

    return () => clearInterval(interval);
  }, []);

  // ─── Cleanup on unmount ───

  useEffect(() => {
    return () => {
      for (const [, session] of sessions.current) {
        session.ws?.close();
      }
      for (const [, interval] of pingIntervals.current) {
        clearInterval(interval);
      }
      for (const [, timeout] of switchAckTimeouts.current) {
        clearTimeout(timeout);
      }
    };
  }, []);

  // ─── Derived state for the active session ───

  const activeSession = getActiveSession();
  const messages = activeSession?.messages ?? [];
  const agentState = activeSession?.agentState ?? 'idle';
  const status = activeSession?.status ?? '';
  // New chat (null activeConversationId) is ready for input — don't show 'disconnected'
  const connectionStatus: ConnectionStatus = activeConversationId === null
    ? 'connected'
    : activeSession?.connectionStatus ?? 'disconnected';

  return {
    activeConversationId,
    conversations,
    messages,
    agentState,
    status,
    connectionStatus,
    sendMessage,
    sendFrame,
    cancelExecution,
    handleNewConversation,
    handleDeleteConversation,
    switchToConversation,
    fetchConversations,
    setMessages,
    setConversations,
  };
}
